import { useEffect, useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useInvalidateSettings } from "@/hooks/useSiteSettings";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Save, Globe, Phone, Share2, MessageCircle, Upload, X, Loader2 } from "lucide-react";

interface SettingsForm {
  site_title: string;
  site_description: string;
  header_logo_url: string;
  footer_logo_url: string;
  favicon_url: string;
  phone: string;
  email: string;
  address: string;
  facebook_url: string;
  instagram_url: string;
  tiktok_url: string;
  youtube_url: string;
  whatsapp_number: string;
  whatsapp_enabled: string;
  whatsapp_message: string;
}

const defaultForm: SettingsForm = {
  site_title: "",
  site_description: "",
  header_logo_url: "",
  footer_logo_url: "",
  favicon_url: "",
  phone: "",
  email: "",
  address: "",
  facebook_url: "",
  instagram_url: "",
  tiktok_url: "",
  youtube_url: "",
  whatsapp_number: "",
  whatsapp_enabled: "false",
  whatsapp_message: "",
};

function ImageUploadField({
  label,
  value,
  onChange,
  folder,
}: {
  label: string;
  value: string;
  onChange: (url: string) => void;
  folder: string;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const ext = file.name.split(".").pop();
    const path = `${folder}/${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from("site-assets").upload(path, file, { upsert: true });
    if (error) {
      setUploading(false);
      return;
    }
    const { data: urlData } = supabase.storage.from("site-assets").getPublicUrl(path);
    onChange(urlData.publicUrl);
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex gap-2">
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://... o sube una imagen"
          className="flex-1"
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
          title="Subir imagen"
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
        </Button>
        {value && (
          <Button type="button" variant="ghost" size="icon" onClick={() => onChange("")} title="Quitar">
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />
      {value && (
        <div className="p-3 bg-muted rounded-lg">
          <img src={value} alt={`${label} preview`} className="h-12 w-auto" />
        </div>
      )}
    </div>
  );
}

export function SettingsSection() {
  const [form, setForm] = useState<SettingsForm>({ ...defaultForm });
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const invalidate = useInvalidateSettings();

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from("settings").select("key, value");
      if (data) {
        const map: Record<string, string> = {};
        data.forEach((r) => (map[r.key] = r.value));
        setForm((prev) => ({ ...prev, ...map } as SettingsForm));
      }
    })();
  }, []);

  const set = (key: keyof SettingsForm, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    const entries = Object.entries(form);
    for (const [key, value] of entries) {
      const { data: existing } = await supabase
        .from("settings")
        .select("id")
        .eq("key", key)
        .maybeSingle();
      if (existing) {
        await supabase.from("settings").update({ value }).eq("key", key);
      } else {
        await supabase.from("settings").insert({ key, value });
      }
    }
    invalidate();
    toast({ title: "Configuración guardada" });
    setSaving(false);
  };

  const renderField = (label: string, k: keyof SettingsForm, placeholder?: string, type: string = "text") => (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input value={form[k]} onChange={(e) => set(k, e.target.value)} placeholder={placeholder} type={type} />
    </div>
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Configuración del Sitio</h2>
          <p className="text-muted-foreground text-sm mt-1">Personaliza la información de tu sitio web</p>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          <Save className="h-4 w-4 mr-1" />
          {saving ? "Guardando..." : "Guardar Todo"}
        </Button>
      </div>

      <Tabs defaultValue="general" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="general" className="gap-1"><Globe className="h-4 w-4" /> General</TabsTrigger>
          <TabsTrigger value="contact" className="gap-1"><Phone className="h-4 w-4" /> Contacto</TabsTrigger>
          <TabsTrigger value="social" className="gap-1"><Share2 className="h-4 w-4" /> Redes</TabsTrigger>
          <TabsTrigger value="whatsapp" className="gap-1"><MessageCircle className="h-4 w-4" /> WhatsApp</TabsTrigger>
        </TabsList>

        <TabsContent value="general">
          <Card>
            <CardHeader><CardTitle>Información General</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {renderField("Nombre del Sitio", "site_title", "Newline Shuttle")}
              <div className="space-y-1.5">
                <Label>Descripción</Label>
                <Textarea value={form.site_description} onChange={(e) => set("site_description", e.target.value)} placeholder="Descripción del sitio para el footer" rows={3} />
              </div>
              <ImageUploadField label="Logo Header" value={form.header_logo_url} onChange={(url) => set("header_logo_url", url)} folder="logos" />
              <ImageUploadField label="Logo Footer" value={form.footer_logo_url} onChange={(url) => set("footer_logo_url", url)} folder="logos" />
              <ImageUploadField label="Favicon" value={form.favicon_url} onChange={(url) => set("favicon_url", url)} folder="favicon" />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="contact">
          <Card>
            <CardHeader><CardTitle>Información de Contacto</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {renderField("Teléfono", "phone", "+1 (809) 123-4567")}
              {renderField("Email", "email", "info@example.com", "email")}
              {renderField("Dirección", "address", "Punta Cana, Dominican Republic")}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="social">
          <Card>
            <CardHeader><CardTitle>Redes Sociales</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {renderField("Facebook", "facebook_url", "https://facebook.com/...")}
              {renderField("Instagram", "instagram_url", "https://instagram.com/...")}
              {renderField("TikTok", "tiktok_url", "https://tiktok.com/...")}
              {renderField("YouTube", "youtube_url", "https://youtube.com/...")}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="whatsapp">
          <Card>
            <CardHeader><CardTitle>WhatsApp Widget</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Widget Activo</Label>
                <Switch checked={form.whatsapp_enabled === "true"} onCheckedChange={(v) => set("whatsapp_enabled", v ? "true" : "false")} />
              </div>
              {renderField("Número (sin +, sin espacios)", "whatsapp_number", "18091234567")}
              <div className="space-y-1.5">
                <Label>Mensaje Predeterminado</Label>
                <Textarea value={form.whatsapp_message} onChange={(e) => set("whatsapp_message", e.target.value)} placeholder="Hello! I need a transfer..." rows={3} />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
